import api from './api';

// =====================================================================
// Öffentliches Slider-CMS (erster Web-Renderer, 2026-09-15)
// Backend: GET /api/sliders/location/:location → Slider[] (nur aktive, direkte Liste)
//          GET /api/slides/slider/:sliderId   → Slide[]  (nur aktive, direkte Liste)
//          GET /api/sponsors/active           → Sponsor[]
// Locations sitzen paarweise an derselben Stelle in Web UND App (Marcel).
// =====================================================================

export interface PublicSlider {
  id: string;
  name: string;
  location: string;
  active: boolean;
  order: number;
  autoSlideDuration?: number | null;
}

export type PublicSlideType = 'manual' | 'sponsor';

export interface PublicSlide {
  id: string;
  sliderId: string;
  type: PublicSlideType;
  order: number;
  active: boolean;
  image?: string | null;
  title?: string | null;
  subtitle?: string | null;
  linkUrl?: string | null;
  buttonText?: string | null;
  aspectRatio?: string | null;
  sponsorIds?: string[] | null;
  /** 2026-09-15: Legacy-Bool */
  desktopOnly?: boolean | null;
  /** 2026-09-15: 'all' | 'desktop' | 'mobile' (Desktop = ≥ 1024 px) */
  visibility?: 'all' | 'desktop' | 'mobile' | null;
  /** 2026-09-15: Bild für den Light Mode */
  imageLight?: string | null;
}

export interface PublicSponsor {
  id: string;
  name?: string | null;
  image?: string | null;
  url?: string | null;
  active?: boolean;
  order?: number;
}

export interface LoadedSlider {
  slider: PublicSlider;
  slides: PublicSlide[];
  sponsors: PublicSponsor[];
}

const DEFAULT_AUTO_MS = 5000;

/** PURE: "16:9" → 16/9; ungültig/leer → Fallback. */
export const parseAspectRatio = (value?: string | null, fallback = 16 / 9): number => {
  const m = /^\s*(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/.exec(value ?? '');
  if (!m) return fallback;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w > 0 && h > 0 ? w / h : fallback;
};

/** PURE: Sekunden (null = 5 s, min. 1 s) → Millisekunden. */
export const autoSlideMs = (seconds?: number | null): number => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return DEFAULT_AUTO_MS;
  return Math.max(1, s) * 1000;
};

/** PURE: nur aktive Slides, nach order sortiert; Sponsor-Slides ohne Sponsoren fliegen raus. */
export const usableSlides = (slides: PublicSlide[], sponsors: PublicSponsor[]): PublicSlide[] => {
  const sponsorIds = new Set(sponsors.filter((s) => s.active !== false).map((s) => s.id));
  return (Array.isArray(slides) ? slides : [])
    .filter((s) => s && s.active !== false)
    .filter((s) => (s.type === 'sponsor' ? (s.sponsorIds ?? []).some((id) => sponsorIds.has(id)) : Boolean(s.image)))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

/** PURE: Sichtbarkeit auflösen – visibility gewinnt, Legacy desktopOnly → 'desktop'. */
export const slideVisibility = (s: Pick<PublicSlide, 'visibility' | 'desktopOnly'>): 'all' | 'desktop' | 'mobile' =>
  s.visibility === 'desktop' || s.visibility === 'mobile' ? s.visibility : s.desktopOnly ? 'desktop' : 'all';

/** PURE: Slides für das aktuelle Gerät (Überall / Nur Desktop / Nur Mobile). */
export const visibleSlides = (slides: PublicSlide[], { isDesktop }: { isDesktop: boolean }): PublicSlide[] =>
  (Array.isArray(slides) ? slides : []).filter((s) => {
    const v = slideVisibility(s);
    return v === 'all' || (v === 'desktop' && isDesktop) || (v === 'mobile' && !isDesktop);
  });

/** PURE: Bild für das aktuelle Theme – Light-Bild nur im hellen Theme und nur wenn vorhanden. */
export const slideImageFor = (slide: Pick<PublicSlide, 'image' | 'imageLight'>, isLight: boolean): string | null =>
  (isLight && slide.imageLight ? slide.imageLight : slide.image) ?? null;

/** PURE: externer Link? (alles außer eigener Domain/relativ) */
export const isExternalLink = (url?: string | null): boolean => {
  const u = (url ?? '').trim();
  if (!u || u.startsWith('/')) return false;
  try {
    const host = new URL(u).hostname.toLowerCase();
    return !(host === 'dartcloudapp.de' || host.endsWith('.dartcloudapp.de'));
  } catch {
    return false;
  }
};

/** PURE: externe DartCloud-URL → interner Pfad, sonst unverändert. */
export const toInternalPath = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
  } catch {
    return url;
  }
};

/**
 * Vorschau-Rahmen je Location (2026-09-27, Admin-Slide-Vorschau): so breit/hoch, wie der
 * Slider an dieser Stelle auf der Website sitzt – Desktop (≥ 1024 px) und Mobile.
 * Quelle: die DynamicSlider-Einbindungen (lg:w-[420px] Hero rechts, lg:w-[400px] neben
 * „Neue Spieler“, Inline-Karten mit fester Kartenhöhe, Mobile volle Breite ≈ 360 px).
 */
export interface SliderPreviewFrame {
  /** Feste Breite in px (Höhe folgt dem Seitenverhältnis) … */
  width?: number;
  /** … oder feste Höhe in px (Breite folgt dem Seitenverhältnis – Inline-Karten) */
  height?: number;
  /** Kurzbeschreibung der Stelle */
  label: string;
}
export interface SliderPreviewFrames { desktop: SliderPreviewFrame; mobile: SliderPreviewFrame }

const MOBILE_FRAME: SliderPreviewFrame = { width: 360, label: 'Mobile: volle Breite' };
const HERO_RIGHT: SliderPreviewFrames = { desktop: { width: 420, label: 'Desktop: Header rechts (420 px)' }, mobile: MOBILE_FRAME };
const PREVIEW_FRAMES: Record<string, SliderPreviewFrames> = {
  home_new_players: { desktop: { width: 400, label: 'Desktop: neben „Neue Spieler“ (400 px)' }, mobile: { width: 360, label: 'Mobile: unter „Neue Spieler“' } },
  home_news_lead: { desktop: { width: 400, label: 'Desktop: rechts neben dem Hauptartikel' }, mobile: { width: 360, label: 'Mobile: unter dem Hauptartikel' } },
  new_entries_home: { desktop: { height: 248, label: 'Desktop: Inline-Karte (Kartenhöhe 248 px)' }, mobile: { width: 360, label: 'Mobile: unter der Sektion' } },
  forum_after_first: { desktop: { width: 600, label: 'Desktop: Themenliste zwischen 1. und 2. Thema' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 1. und 2. Thema' } },
  home_prediction_games: { desktop: { width: 400, label: 'Desktop: rechts neben den Tippspiel-Karten (400 px)' }, mobile: { width: 360, label: 'Mobile: unter der Sektion „Tippspiele“' } },
  home_pdc_calendar: { desktop: { height: 248, label: 'Desktop: Inline-Karte (Kartenhöhe)' }, mobile: { width: 360, label: 'Mobile: unter der Sektion' } },
  tippspiele_sidebar: { desktop: { width: 600, label: 'Desktop: zwischen „Meine Tippspiele“ und „Alle Tippspiele“' }, mobile: { width: 360, label: 'Mobile: zwischen „Meine Tippspiele“ und „Alle Tippspiele“' } },
  login: { desktop: { height: 200, label: 'App-Dialog (Höhe 200 px)' }, mobile: { height: 200, label: 'App-Dialog (Höhe 200 px)' } },
  feed_after_first: { desktop: { width: 600, label: 'Desktop: Feed-Spalte zwischen 1. und 2. Beitrag' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 1. und 2. Beitrag' } },
  for_you_after_first: { desktop: { width: 600, label: 'Desktop: „Für dich“ zwischen 1. und 2. Beitrag' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 1. und 2. Beitrag' } },
  feed_after_fourth: { desktop: { width: 600, label: 'Desktop: Feed-Spalte zwischen 4. und 5. Beitrag' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 4. und 5. Beitrag' } },
  pdc_series_after_second: { desktop: { width: 600, label: 'Desktop: Serien-Liste zwischen 2. und 3. Serie' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 2. und 3. Serie' } },
  pro_series_after_second: { desktop: { width: 600, label: 'Desktop: Serien-Liste zwischen 2. und 3. Serie' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 2. und 3. Serie' } },
  for_you_after_fourth: { desktop: { width: 600, label: 'Desktop: „Für dich“ zwischen 4. und 5. Beitrag' }, mobile: { width: 360, label: 'Mobile: volle Breite zwischen 4. und 5. Beitrag' } },
};

/** PURE: Vorschau-Rahmen einer Location; unbekannte Locations = Hero rechts / Mobile volle Breite. */
export const previewFramesForLocation = (location?: string | null): SliderPreviewFrames =>
  PREVIEW_FRAMES[(location ?? '').trim()] ?? HERO_RIGHT;

export const buildSlideImageUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

const cache = new Map<string, { at: number; promise: Promise<LoadedSlider | null> }>();
const CACHE_TTL_MS = 5 * 60 * 1000;

class SliderService {
  /** Slider + aktive Slides (+ Sponsoren, falls nötig) einer Location; null, wenn nichts zu zeigen ist. */
  async loadByLocation(location: string, { force = false } = {}): Promise<LoadedSlider | null> {
    const hit = cache.get(location);
    if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;
    const promise = this.fetchByLocation(location).catch(() => null);
    cache.set(location, { at: Date.now(), promise });
    return promise;
  }

  private async fetchByLocation(location: string): Promise<LoadedSlider | null> {
    const res = await api.get(`/api/sliders/location/${encodeURIComponent(location)}`);
    const raw = res.data;
    const list: PublicSlider[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    const slider = list.filter((s) => s && s.active !== false).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
    if (!slider) return null;
    const slidesRes = await api.get(`/api/slides/slider/${encodeURIComponent(slider.id)}`);
    const slidesRaw = slidesRes.data;
    const slides: PublicSlide[] = Array.isArray(slidesRaw) ? slidesRaw : Array.isArray(slidesRaw?.data) ? slidesRaw.data : [];
    let sponsors: PublicSponsor[] = [];
    if (slides.some((s) => s.type === 'sponsor')) {
      try {
        const spRes = await api.get('/api/sponsors/active');
        sponsors = Array.isArray(spRes.data) ? spRes.data : Array.isArray(spRes.data?.data) ? spRes.data.data : [];
      } catch {
        sponsors = [];
      }
    }
    const usable = usableSlides(slides, sponsors);
    if (usable.length === 0) return null;
    return { slider, slides: usable, sponsors };
  }

  clearCache(): void {
    cache.clear();
  }
}

const sliderService = new SliderService();
export default sliderService;
